import { useEffect, useRef, useState } from 'react';
import { txApi } from '../txApi.js';
import { formulaField, insertFormulaText, starterFormula } from '../utmFormula.js';

function Diagnostics({ result }) {
  return result?.diagnostics?.map((d, i) => <p key={i} role="alert" className="utm-issue">Line {d.line}, column {d.column}: {d.message}</p>);
}

export default function UtmFormulaEditor({ clientId, setup, fields, readOnly, onSave, onClose }) {
  const initial = setup.formula;
  const [expression, setExpression] = useState(initial?.expression || '');
  const [samples, setSamples] = useState(initial?.samples || {});
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [tab, setTab] = useState('fields');
  const [search, setSearch] = useState('');
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [result, setResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [references, setReferences] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const modal = useRef(null), testModal = useRef(null), editor = useRef(null);
  const alive = useRef(true);
  const dirty = expression !== (initial?.expression || '') || JSON.stringify(samples) !== JSON.stringify(initial?.samples || {});
  useEffect(() => {
    alive.current = true;
    const previous = document.activeElement;
    modal.current.showModal(); editor.current.focus();
    return () => { alive.current = false; previous?.focus(); };
  }, []);
  useEffect(() => {
    let current = true;
    setCatalogError('');
    txApi.getUtmFormulaFunctions(clientId).then(d => { if (current) setCatalog(d.functions); })
      .catch(e => { if (current) setCatalogError(e.message); });
    return () => { current = false; };
  }, [clientId, catalogAttempt]);
  useEffect(() => {
    const leave = e => { if (!readOnly && dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', leave);
    return () => window.removeEventListener('beforeunload', leave);
  }, [dirty, readOnly]);
  useEffect(() => { if (references) testModal.current?.showModal(); }, [references]);
  const close = () => {
    if (busy) return;
    if (!readOnly && dirty && !window.confirm('Discard unsaved formula and sample changes?')) return;
    onClose();
  };
  const updateExpression = value => { setExpression(value); setResult(null); setError(''); };
  const insert = value => {
    if (readOnly || busy) return;
    const input = editor.current;
    const next = insertFormulaText(expression, input.selectionStart, input.selectionEnd, value);
    updateExpression(next.expression);
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(next.cursor, next.cursor); });
  };
  const request = test => txApi.testUtmFormula(clientId, { version_id: setup.id, expression, samples, test });
  const run = async action => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      if (action === 'save' && !expression.trim()) {
        if (await onSave(null)) onClose();
        return;
      }
      const d = await request(action === 'evaluate');
      if (!alive.current) return;
      if (action === 'evaluate') setTestResult(d);
      else {
        setResult(d);
        if (d.valid && action === 'test') { setTestResult(null); setReferences(d.references); }
        if (d.valid && action === 'save') {
          if (await onSave({ expression, samples, language_version: 1 })) onClose();
        }
      }
    } catch (e) { if (alive.current) setError(e.message); }
    finally { if (alive.current) setBusy(false); }
  };
  const closeTest = () => { if (!busy) { testModal.current.close(); setReferences(null); } };
  const filteredFields = fields.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));
  const functions = catalog.filter(f => `${f.name} ${f.description}`.toLowerCase().includes(search.toLowerCase()));
  return <>
    <dialog ref={modal} className="o-modal utm-formula-dialog" aria-labelledby="utm-formula-title" onCancel={e => { e.preventDefault(); close(); }}>
      <div className="o-modal-head"><h2 id="utm-formula-title">URL generation formula</h2><button className="o-x" aria-label="Close formula editor" disabled={busy} onClick={close}>×</button></div>
      <div className="o-modal-body">
        <p className="o-note">Use UTM fields to build the complete URL. The same formula applies to every platform using its field mappings.</p>
        {readOnly && <p className="o-hint">Archived version. You can test this formula; restore the version to save changes.</p>}
        <div className="utm-formula-layout">
          <div className="utm-expression-panel">
            <label className="o-field-lbl" htmlFor="utm-expression">Expression</label>
            <div className="utm-expression-control">
              <div className="utm-expression-gutter" aria-hidden="true"><div style={{ transform: `translateY(-${scrollTop}px)` }}>{expression.split('\n').map((_, i) => <div key={i}>{i + 1}</div>)}</div></div>
              <textarea id="utm-expression" ref={editor} className="o-control o-mono" spellCheck="false" wrap="off" value={expression} readOnly={readOnly || busy} maxLength={16384}
                onScroll={e => setScrollTop(e.target.scrollTop)} onChange={e => updateExpression(e.target.value)} />
            </div>
            <p className="o-note">Join the destination and parameter fields with &amp; to add their parameter names automatically, including after text extraction. Values are URL-encoded. Use BUILDURL for custom parameter names; explicit query text keeps your manual formatting.</p>
            <div className="utm-toolbar">
              {!readOnly && <button className="o-btn o-btn-sm" disabled={busy || !fields.some(f => f.role === 'destination')} onClick={() => { if (!expression || window.confirm('Replace the expression with a starter formula?')) updateExpression(starterFormula(fields)); }}>Insert starter formula</button>}
              <button className="o-btn o-btn-sm" disabled={busy || !expression.trim()} onClick={() => run('validate')}>Validate</button>
              <button className="o-btn o-btn-sm" disabled={busy || !expression.trim()} onClick={() => run('test')}>Test</button>
            </div>
          </div>
          <aside className="utm-formula-library" aria-label="Formula library">
            <label className="o-field-lbl" htmlFor="utm-formula-search">Search fields and functions</label>
            <input id="utm-formula-search" className="o-control" value={search} onChange={e => setSearch(e.target.value)} />
            <div className="o-chipset" aria-label="Formula library category">
              <button className="o-chip" aria-pressed={tab === 'fields'} onClick={() => setTab('fields')}>Fields ({fields.length})</button>
              <button className="o-chip" aria-pressed={tab === 'functions'} onClick={() => setTab('functions')}>Functions ({catalog.length})</button>
            </div>
            <div className="utm-formula-items">
              {tab === 'fields' ? filteredFields.map(f => <button className="o-btn o-btn-ghost" key={f.id} disabled={readOnly || busy} onClick={() => insert(formulaField(f.name))}>{f.name}</button>)
                : functions.map(f => <button className="o-btn o-btn-ghost" key={f.name} aria-pressed={selectedFunction?.name === f.name} onClick={() => setSelectedFunction(f)}>{f.name}</button>)}
              {tab === 'fields' && !filteredFields.length && <p className="o-note">No matching fields.</p>}
              {tab === 'functions' && !functions.length && !catalogError && <p className="o-note">{catalog.length ? 'No matching functions.' : 'Loading functions…'}</p>}
            </div>
          </aside>
        </div>
        {catalogError && <p role="alert" className="utm-issue">{catalogError} <button className="o-btn o-btn-sm" onClick={() => setCatalogAttempt(n => n + 1)}>Retry</button></p>}
        {tab === 'functions' && selectedFunction && <div className="utm-function-help"><code>{selectedFunction.signature}</code><p>{selectedFunction.description}</p><code>{selectedFunction.example}</code><button className="o-btn o-btn-sm" disabled={readOnly || busy} onClick={() => insert(`${selectedFunction.name}(`)}>Insert function</button></div>}
        <Diagnostics result={result} />
        {result?.valid && <p role="status" className="o-note">Formula syntax is valid. Test with sample values to check its output.</p>}
        {error && <p role="alert" className="utm-issue">{error}</p>}
        {!readOnly && !expression.trim() && <p className="o-note">Saving an empty expression clears the formula and stops new URL generation. Existing URLs are preserved.</p>}
      </div>
      <div className="o-modal-foot"><button className="o-btn" disabled={busy} onClick={close}>{readOnly ? 'Close' : 'Cancel'}</button>{!readOnly && <button className="o-btn o-btn-primary" disabled={busy} onClick={() => run('save')}>{busy ? 'Working…' : 'Save formula'}</button>}</div>
    </dialog>
    {references && <dialog ref={testModal} className="o-modal utm-formula-test" aria-labelledby="utm-formula-test-title"
      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeTest(); } }}
      onCancel={e => { e.preventDefault(); e.stopPropagation(); closeTest(); }}>
      <div className="o-modal-head"><h2 id="utm-formula-test-title">Test formula</h2><button className="o-x" aria-label="Close formula test" disabled={busy} onClick={closeTest}>×</button></div>
      <div className="o-modal-body">
        <p className="o-note">Enter sample values for the fields used by this formula. This checks the expression; campaign rows also validate required fields and available source values.</p>
        <div className="utm-sample-fields">{references.map(f => <label key={f.id}>{f.name}{f.role === 'destination' ? <span className="o-note">Destination URL</span> : f.parameter && f.parameter !== f.name ? <span className="o-note">{f.parameter}</span> : null}{f.field_type === 'date' && <span className="o-note">YYYY-MM-DD</span>}<input className="o-control" value={samples[f.id] || ''} disabled={busy} maxLength={65536} onChange={e => { setSamples(s => ({ ...s, [f.id]: e.target.value })); setTestResult(null); }} /></label>)}</div>
        {!references.length && <p className="o-note">This formula does not reference any fields.</p>}
        <Diagnostics result={testResult} />
        <label className="utm-formula-output">Output<textarea className="o-control o-mono" readOnly rows="4" value={testResult?.output || ''} /></label>
        {testResult?.valid && <p role="status" className="o-note">{testResult.output ? 'Formula evaluated successfully.' : 'No URL generated. The formula returned an empty value.'}</p>}
        {error && <p role="alert" className="utm-issue">{error}</p>}
        <p className="o-note">{readOnly ? 'Sample changes are temporary for this archived version.' : 'Save the formula to keep these samples with this setup version.'}</p>
      </div>
      <div className="o-modal-foot"><button className="o-btn" disabled={busy} onClick={closeTest}>Back to editor</button><button className="o-btn o-btn-primary" disabled={busy} onClick={() => run('evaluate')}>{busy ? 'Testing…' : 'Run test'}</button></div>
    </dialog>}
  </>;
}
